import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ProgressBarModule } from 'primeng/progressbar';
import { Subject, takeUntil, timer, switchMap, takeWhile, filter } from 'rxjs';
import { AnalysisService } from '@core/services/analysis.service';
import { ChatNotificationWsService } from '@core/services/chat-notification-ws.service';
import { AnalysisChatStateService } from '@core/services/analysis-chat-state.service';
import { ChatMessagesService } from '@core/services/chat-messages.service';
import { ChatMessageDTO, ChatSessionDTO, MessageSender } from '@core/models/chat.models';

@Component({
  selector: 'app-analysis-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextareaModule, ProgressBarModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './analysis-chat.component.html',
  styleUrl: './analysis-chat.component.scss'
})
export class AnalysisChatComponent implements OnInit, OnDestroy {
  @Input() solicitudId!: string;
  @Input() usuarioId!: string;
  @Input() chatId: string | null = null;

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('messageInput') messageInput!: ElementRef;

  messages: ChatMessageDTO[] = [];
  newMessage = '';
  isLoading = true;
  isConnected = false;
  isTyping = false;
  isSending = false;
  assistantName = 'Asistente IA';
  completenessScore = 0;
  error: string | null = null;

  private destroy$ = new Subject<void>();
  private isPolling = false;

  constructor(
    private analysisService: AnalysisService,
    private chatWsService: ChatNotificationWsService,
    private chatStateService: AnalysisChatStateService,
    private chatMessagesService: ChatMessagesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setupWebSocketListeners();

    if (this.chatId) {
      this.subscribeToSharedMessages(this.chatId);
      this.loadExistingChat(this.chatId);
    } else if (this.solicitudId) {
      this.findChatBySolicitud();
    }
  }

  /**
   * Suscribirse al store compartido de mensajes.
   * Permite sincronizar mensajes entre la página y la burbuja del chat.
   */
  private subscribeToSharedMessages(chatId: string): void {
    this.chatMessagesService.getMessages$(chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(messages => {
        if (messages.length > 0 && messages.length !== this.messages.length) {
          this.messages = messages;
          this.cdr.markForCheck();
          this.scrollToBottom();
        }
      });

    // Restaurar metadata si otra instancia ya la cargó
    const meta = this.chatMessagesService.getMetadata(chatId);
    if (meta) {
      this.assistantName = meta.assistantName;
      this.completenessScore = meta.completenessScore;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    if (this.chatId) {
      this.chatWsService.unsubscribeFromChat(this.chatId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupWebSocketListeners(): void {
    this.chatWsService.connected$
      .pipe(takeUntil(this.destroy$))
      .subscribe(connected => {
        this.isConnected = connected;
        if (connected) {
          this.stopPolling();
        }
        this.cdr.markForCheck();
      });

    this.chatWsService.chatMessage$
      .pipe(
        takeUntil(this.destroy$),
        filter(msg => msg.chatId === this.chatId)
      )
      .subscribe(({ message }) => {
        if (this.chatId) {
          this.chatMessagesService.addMessage(this.chatId, message);
        }
        this.isTyping = false;
        this.stopPolling();
        this.cdr.markForCheck();
        this.scrollToBottom();
      });

    this.chatWsService.chatTyping$
      .pipe(
        takeUntil(this.destroy$),
        filter(msg => msg.chatId === this.chatId)
      )
      .subscribe(({ typing }) => {
        this.isTyping = typing.isTyping;
        this.cdr.markForCheck();
        if (typing.isTyping) {
          this.scrollToBottom();
        }
      });
  }

  // === Polling: mecanismo principal para recibir respuestas ===
  private startPolling(): void {
    if (this.isPolling || !this.chatId) return;
    this.isPolling = true;

    const knownCount = this.messages.length;

    timer(2000, 3000).pipe(
      takeUntil(this.destroy$),
      takeWhile(() => this.isPolling),
      switchMap(() => this.analysisService.getChatSession(this.chatId!))
    ).subscribe({
      next: (session: ChatSessionDTO) => {
        if (session.messages && session.messages.length > knownCount) {
          this.chatMessagesService.setMessages(this.chatId!, session.messages);
          this.isTyping = false;
          this.stopPolling();
          this.cdr.markForCheck();
          this.scrollToBottom();
        }
        this.assistantName = session.assistantName || this.assistantName;
        this.completenessScore = session.completenessScore || 0;
        if (this.chatId) {
          this.chatMessagesService.setMetadata(this.chatId, this.assistantName, this.completenessScore);
        }
      },
      error: () => { /* silencioso */ }
    });
  }

  private stopPolling(): void {
    this.isPolling = false;
  }

  // === Inicialización ===
  private findChatBySolicitud(): void {
    this.isLoading = true;
    this.error = null;

    this.analysisService.getChatBySolicitud(this.solicitudId).subscribe({
      next: (session: ChatSessionDTO) => {
        if (session && session.chatId) {
          this.chatId = session.chatId;
          this.chatStateService.setChatId(session.chatId);
          this.messages = session.messages || [];
          this.assistantName = session.assistantName || 'Asistente IA';
          this.completenessScore = session.completenessScore || 0;
          this.chatMessagesService.setMessages(session.chatId, this.messages);
          this.chatMessagesService.setMetadata(session.chatId, this.assistantName, this.completenessScore);
          this.subscribeToSharedMessages(session.chatId);
          this.isLoading = false;
          this.isConnected = true;
          if (this.messages.length === 0) {
            this.isTyping = true;
            this.startPolling();
          }
          this.cdr.markForCheck();
          this.scrollToBottom();
          this.connectWebSocket();
        } else {
          this.handleNoChatFound();
        }
      },
      error: () => {
        this.handleNoChatFound();
      }
    });
  }

  private handleNoChatFound(): void {
    this.isLoading = false;
    this.chatStateService.resetChat();
    this.cdr.markForCheck();
  }

  private startAnalysis(): void {
    this.isLoading = true;
    this.error = null;

    this.analysisService.startAnalysis({
      solicitudId: this.solicitudId
    }).subscribe({
      next: (response) => {
        this.chatId = response.interviewerChatId;
        this.chatStateService.setChatId(response.interviewerChatId);
        this.subscribeToSharedMessages(response.interviewerChatId);
        this.isLoading = false;
        this.isTyping = true;
        this.cdr.markForCheck();
        this.connectWebSocket();
        this.startPolling();
      },
      error: (err) => {
        this.isLoading = false;
        this.error = err?.error?.mensaje || err?.error?.message || 'Error al iniciar el análisis';
        this.cdr.markForCheck();
      }
    });
  }

  private loadExistingChat(chatId: string): void {
    this.isLoading = true;
    this.analysisService.getChatSession(chatId).subscribe({
      next: (session: ChatSessionDTO) => {
        this.messages = session.messages || [];
        this.assistantName = session.assistantName || 'Asistente IA';
        this.completenessScore = session.completenessScore || 0;
        this.chatMessagesService.setMessages(chatId, this.messages);
        this.chatMessagesService.setMetadata(chatId, this.assistantName, this.completenessScore);
        this.isLoading = false;
        this.isConnected = true;
        if (this.messages.length === 0) {
          this.isTyping = true;
          this.startPolling();
        }
        this.cdr.markForCheck();
        this.scrollToBottom();
        this.connectWebSocket();
      },
      error: () => {
        this.isLoading = false;
        this.isTyping = true;
        this.cdr.markForCheck();
        this.connectWebSocket();
        this.startPolling();
      }
    });
  }

  private connectWebSocket(): void {
    if (this.chatId) {
      this.chatWsService.subscribeToChat(this.chatId);
    }
  }

  // === Envío de mensajes (REST) ===
  sendMessage(): void {
    const content = this.newMessage.trim();
    if (!content || !this.chatId || this.isSending) return;

    this.isSending = true;

    const clientMessage: ChatMessageDTO = {
      id: crypto.randomUUID(),
      sender: 'CLIENT' as MessageSender,
      content: content,
      messageType: 'TEXT',
      fechaCreacion: new Date().toISOString()
    };

    this.chatMessagesService.addMessage(this.chatId, clientMessage);
    this.newMessage = '';
    this.isTyping = true;
    this.cdr.markForCheck();
    this.scrollToBottom();

    this.analysisService.sendMessage(this.chatId, content).subscribe({
      next: () => {
        this.isSending = false;
        // Iniciar polling para recibir la respuesta del asistente
        this.startPolling();
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSending = false;
        this.startPolling();
        this.cdr.markForCheck();
      }
    });
  }

  selectOption(option: string): void {
    if (!this.chatId) return;

    const clientMessage: ChatMessageDTO = {
      id: crypto.randomUUID(),
      sender: 'CLIENT' as MessageSender,
      content: option,
      messageType: 'TEXT',
      fechaCreacion: new Date().toISOString()
    };

    this.chatMessagesService.addMessage(this.chatId, clientMessage);
    this.isTyping = true;
    this.cdr.markForCheck();
    this.scrollToBottom();

    this.analysisService.sendMessage(this.chatId, option).subscribe({
      next: () => this.startPolling(),
      error: () => this.startPolling()
    });
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  isAssistant(sender: MessageSender): boolean {
    return sender === 'ASSISTANT';
  }

  isClient(sender: MessageSender): boolean {
    return sender === 'CLIENT';
  }

  isSystem(sender: MessageSender): boolean {
    return sender === 'SYSTEM';
  }

  getProgressPercent(): number {
    return Math.round(this.completenessScore * 100);
  }

  retryConnection(): void {
    this.error = null;
    if (this.chatId) {
      this.loadExistingChat(this.chatId);
    } else if (this.solicitudId) {
      this.findChatBySolicitud();
    }
  }

  trackByMessageId(index: number, msg: ChatMessageDTO): string {
    return msg.id;
  }

  formatMessage(content: string): string {
    if (!content) return '';
    return content
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  }

  formatTime(fecha: string): string {
    if (!fecha) return '';
    try {
      const date = new Date(fecha);
      return date.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messagesContainer?.nativeElement) {
        const el = this.messagesContainer.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 100);
  }
}
